import { h } from "preact";
import { fontFamilyStyle } from "./monogram-fonts.js";
import {
  DEFAULT_PLACEMENT_SCALE,
  HAND_PAINTED,
  anchorEdges,
  fontScale,
  positionsOf,
  scaleOf,
  splitText,
} from "./monogram-options.js";

// Font size, as a share of the image's width, at scale 100 on both the
// placement and the font.
const BASE_SIZE_CQW = 20;

/** Pins the anchored point of the letters to the position's percentages. */
function positionStyle(position) {
  const edges = anchorEdges(position);
  const x = position.horizontalAnchor === "center" ? "-50%" : "0";
  const y = position.anchor === "center" ? "-50%" : "0";

  return {
    [edges.x]: `${position.left ?? 50}%`,
    [edges.y]: `${position.offset ?? 0}%`,
    transform: `translate(${x}, ${y})`,
  };
}

/**
 * @param {Object} props
 * @param {Function} [props.textProps] - Called with each position and its
 *   index, for extra props on its letters
 * @param {Function} [props.textChildren] - Called likewise, for elements
 *   inside its letters
 */
export default function MonogramPreview({
  config,
  selection,
  text,
  font,
  placement,
  strings,
  textProps,
  textChildren,
  children,
}) {
  const image =
    placement?.useAlternateImage && config.alternateImage
      ? config.alternateImage
      : config.image;

  if (!image) return null;

  const handPainted = selection.type === HAND_PAINTED;
  const colors = config.handPainted?.colors ?? [];
  const firstColor = colors.find(
    (color) => color.id === selection.firstColorId,
  )?.color;
  const secondColor = colors.find(
    (color) => color.id === selection.secondColorId,
  )?.color;

  const size =
    BASE_SIZE_CQW *
    scaleOf(placement?.scale, DEFAULT_PLACEMENT_SCALE) *
    fontScale(font) *
    (handPainted && selection.size === 2 ? 2 : 1);

  const positions = positionsOf(placement);
  const parts = splitText(text ?? "", positions.length);
  const textStyle = {
    fontSize: `${size}cqw`,
    fontFamily: fontFamilyStyle(font?.url),
    color: handPainted ? firstColor : undefined,
    "--monogram-shadow-color": handPainted ? secondColor : undefined,
  };

  return (
    <div
      class="monogram-preview"
      role="group"
      aria-label={window.theme.translations(strings, "preview_label")}
    >
      <div
        class="monogram-preview__box"
        style={{
          "--monogram-image-ratio":
            image.width && image.height ? image.width / image.height : 1,
        }}
      >
        <img
          class="monogram-preview__image"
          src={image.src}
          srcset={image.srcset}
          sizes="(min-width: 1024px) 490px, 100vw"
          width={image.width}
          height={image.height}
          alt={image.alt ?? ""}
        />
        {font &&
          positions.map((position, index) => {
            if (!parts[index]) return null;
            const extraProps = textProps?.(position, index);

            return (
              <div
                key={position.id}
                aria-hidden="true"
                {...extraProps}
                class={`monogram-preview__text monogram-preview__text--${handPainted ? "hand-painted" : "gold-foil"} ${extraProps?.class ?? ""}`}
                style={{ ...textStyle, ...positionStyle(position) }}
              >
                {parts[index]}
                {textChildren?.(position, index)}
              </div>
            );
          })}
        {children}
      </div>
    </div>
  );
}
